import React, { useState, useEffect } from 'react';
import { Field, change } from 'redux-form';
import { connect } from 'react-redux';
import { AutoCompleteSelect, Select } from '../../../../../itrust_common/components/form';
import { contactLensTypeFields } from '../../../../../itrust_common/components/Constants';
import {
  humanize,
  isPresentInArray,
} from '../../../../../itrust_common/components/HelperFunctions';

const FORM_NAME = 'orderContactLensForm';

const QUANTITY_OPTIONS = [1, 2, 3, 4, 5].map((val) => ({ label: String(val), value: val }));

const FIELD_NAME_MAP = {
  base_curves: 'curve',
  diameters: 'diameter',
  spheres: 'sphere',
};

function mapLensFieldName(field) {
  return FIELD_NAME_MAP[field] || field;
}

function TrialContactLensFields({
  member,
  orderStatus,
  currentContactLens,
  pprops,
  orderStoreId,
}) {
  const [isOdLensTypeLoading, setIsOdLensTypeLoading] = useState(false);
  const [isOsLensTypeLoading, setIsOsLensTypeLoading] = useState(false);
  const [odReset, setOdReset] = useState(false);
  const [osReset, setOsReset] = useState(false);
  const [odLensTypeFields, setOdLensTypeFields] = useState([]);
  const [osLensTypeFields, setOsLensTypeFields] = useState([]);

  useEffect(() => {
    syncLensTypeFields();
  }, [
    currentContactLens?.od_contact_lens_attributes?.id,
    currentContactLens?.os_contact_lens_attributes?.id,
  ]);

  useEffect(() => {
    if (!isOdLensTypeLoading) return undefined;
    const timer = setTimeout(() => setIsOdLensTypeLoading(false), 3000);
    return () => clearTimeout(timer);
  }, [isOdLensTypeLoading]);

  useEffect(() => {
    if (!isOsLensTypeLoading) return undefined;
    const timer = setTimeout(() => setIsOsLensTypeLoading(false), 3000);
    return () => clearTimeout(timer);
  }, [isOsLensTypeLoading]);

  const syncLensTypeFields = () => {
    const odAttr = currentContactLens?.od_contact_lens_attributes;
    const osAttr = currentContactLens?.os_contact_lens_attributes;

    setOdReset(false);
    setOsReset(false);
    setOdLensTypeFields(
      odAttr
        ? contactLensTypeFields[odAttr?.lens_type]
        : contactLensTypeFields[currentContactLens?.od_lens_type],
    );
    setOsLensTypeFields(
      osAttr
        ? contactLensTypeFields[osAttr?.lens_type]
        : contactLensTypeFields[currentContactLens?.os_lens_type],
    );
    setIsOdLensTypeLoading(false);
    setIsOsLensTypeLoading(false);
  };

  const getStoreIdForSearch = () => {
    const storeId = localStorage.getItem('StoreID');
    if (!storeId && storeId == null) {
      return orderStoreId;
    }
    return storeId;
  };

  const setLensLoading = (type) => {
    if (type === 'od') {
      setIsOdLensTypeLoading(true);
      setOdReset(true);
    } else {
      setIsOsLensTypeLoading(true);
      setOsReset(true);
    }
  };

  const onContactLensSelect = (type) => {
    setLensLoading(type);
    setTimeout(() => pprops.submit(), 500);
  };

  const buildLensTypeData = (arr) => {
    let data = [];
    arr &&
      arr.map((val) => {
        data = [...data, { key: val, value: val }];
      });
    return data;
  };

  const handleAddSearchValSubmit = (val, contact_lens_id, field_name, type) => {
    const addToSettings = {
      type,
      val,
      field_name,
      setting_contact_lens_id: contact_lens_id,
    };
    setLensLoading(type);
    pprops.dispatch(change(FORM_NAME, `${member}[add_lens_values]`, addToSettings));
    setTimeout(() => pprops.submit(), 500);
  };

  const copyTo = (source, destination) => {
    const sourceAttrs = currentContactLens?.[`${source}_contact_lens_attributes`];
    const sourceLensType = sourceAttrs?.lens_type;
    const sourceLensId = currentContactLens?.[`${source}_contact_lens_id`];

    pprops.dispatch(
      change(FORM_NAME, `${member}[${destination}_contact_lens_attributes]`, sourceAttrs),
    );
    pprops.dispatch(
      change(FORM_NAME, `${member}[${destination}_contact_lens_id]`, sourceLensId),
    );

    (contactLensTypeFields[sourceLensType] || []).forEach((field) => {
      const mappedField = mapLensFieldName(field);
      const sourceFieldVal = currentContactLens?.[`${source}_${mappedField}`] || '';
      pprops.dispatch(
        change(FORM_NAME, `${member}[${destination}_${mappedField}]`, sourceFieldVal),
      );
    });

    if (destination === 'od') {
      setOdLensTypeFields(contactLensTypeFields[sourceLensType]);
      setOdReset(true);
    } else {
      setOsLensTypeFields(contactLensTypeFields[sourceLensType]);
      setOsReset(true);
    }
    setTimeout(() => pprops.submit(), 500);
  };

  const renderEyeLabel = (eye, copyTarget) => (
    <span>
      {eye.toUpperCase()}
      <small
        className="float-right btn-link"
        onClick={() => orderStatus !== 'finalized' && copyTo(eye, copyTarget)}
      >
        <i className="la la-sm la-copy" />
        {copyTarget.toUpperCase()}
      </small>
    </span>
  );

  const renderLensFields = (lensType, fieldName, contactLensAttributes) => {
    const contactLensExamField = mapLensFieldName(fieldName);
    const isShowOnlyExistValPrefill =
      fieldName === 'base_curves' || fieldName === 'diameters';

    return (
      <Field
        name={`${member}[${lensType}_${contactLensExamField}]`}
        label={`${humanize(contactLensExamField)}`}
        component={AutoCompleteSelect}
        reset={lensType === 'od' ? odReset : osReset}
        data={buildLensTypeData(contactLensAttributes && contactLensAttributes[fieldName])}
        textField="value"
        valueField="key"
        wrapperClasses="col-6 col-md-4 col-lg-3 col-xl-2 mb-3"
        key={`${member}[${lensType}_${contactLensExamField}]-${contactLensAttributes?.id}`}
        displayName={`${member}[${lensType}_${contactLensExamField}]`}
        hideResetButton={true}
        className="form-control"
        labelClasses="w-100"
        showOnlyExistValPrefill={isShowOnlyExistValPrefill}
        showAddOption={true}
        disabled={orderStatus === 'finalized'}
        addSearchValSubmit={(val) =>
          handleAddSearchValSubmit(
            val,
            contactLensAttributes?.id,
            contactLensExamField,
            lensType,
          )
        }
      />
    );
  };

  const loadingSpinner = (
    <div className="col-12 col-md-8 d-flex align-items-center mb-3">
      <span
        className="spinner-border text-primary"
        style={{ width: '1.25rem', height: '1.25rem' }}
        role="status"
      />
      <h6 className="d-inline-block pl-3 mb-0">Loading Lens Type...</h6>
    </div>
  );

  const renderEyeRow = (eye, copyTarget, isLoading, lensTypeFields, contactLensAttributes) => (
    <div className="row align-items-start mb-4">
      <div className="col-12 col-md-4 col-xl-3 form-group mb-3">
        <Field
          key={contactLensAttributes?.id}
          name={`${member}[${eye}_contact_lens_id]`}
          displayName={`${member}[${eye}_contact_lens_brand_name]`}
          component={AutoCompleteSelect}
          onChangeHook={() => onContactLensSelect(eye)}
          klass="Settings::ContactLens"
          additionalFilters={{ store_inventory: getStoreIdForSearch() ,
            order_type : 'trial'
          }}
          textField="brand_name"
          valueField="id"
          placeholder="Select Contact Lens"
          className="form-control"
          label={renderEyeLabel(eye, copyTarget)}
          labelClasses="w-100"
          disabled={orderStatus === 'finalized'}
        />
        <small>{contactLensAttributes?.manufacturer_name}</small>
      </div>

      {isLoading ? (
        loadingSpinner
      ) : (
        <div className="col-12 col-md-8 col-xl-9">
          <div className="row">
            {lensTypeFields?.map((fieldName) => (
              <React.Fragment key={fieldName}>
                {isPresentInArray(lensTypeFields, fieldName) &&
                  renderLensFields(eye, fieldName, contactLensAttributes)}
              </React.Fragment>
            ))}
            <Field
              name={`${member}[${eye}_lens_trial_order_quantity]`}
              label="Quantity"
              component={Select}
              data={QUANTITY_OPTIONS}
              textField="label"
              valueField="value"
              className="form-control"
              wrapperClasses="col-6 col-md-4 col-lg-3 col-xl-2 mb-3"
              labelClasses="w-100"
              disabled={orderStatus === 'finalized'}
              onChange={() => setTimeout(() => pprops.submit(), 500)}
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={`${orderStatus === 'finalized' ? 'disabled' : ''}`}>
      {renderEyeRow(
        'od',
        'os',
        isOdLensTypeLoading,
        odLensTypeFields,
        currentContactLens?.od_contact_lens_attributes,
      )}
      {renderEyeRow(
        'os',
        'od',
        isOsLensTypeLoading,
        osLensTypeFields,
        currentContactLens?.os_contact_lens_attributes,
      )}
    </div>
  );
}

const mapStateToProps = (state) => {
  const { order } = state;
  return {
    orderStoreId: order?.order?.store_id,
  };
};

export default connect(mapStateToProps)(TrialContactLensFields);
